/**
 * Global Collect&Go enrich worker — runs whenever Menu + extension are available.
 * Scrapes zoeken + PDPs for products needing candidates; posts results to server.
 */

import { cogoPriceCentsFromText } from './cogoPrice'
import {
  cogoCommand,
  cogoEnsureTab,
  cogoCheckAuth,
  probeCogoExtensionFast,
  isCogoTabBusy,
  withCogoTabLock,
  COGO_SEARCH_URL,
} from './cogoExtension'
import {
  isCogoEnrichEnabled,
  subscribeCogoEnrichEnabled,
} from './cogoEnrichPrefs'
import { beginCogoAutomation, endCogoAutomation } from './cogoTabLifecycle'

/** Idle poll when queue empty, disabled, or waiting on extension/auth. */
const IDLE_MS = 20_000
const TOP_N = 5
/** How many search hits to scan when loading more (skipping excluded). */
const SEARCH_SCAN = 40

function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms))
}

export type EnrichArticle = {
  technicalProductId: string
  display: string
  assortmentUrl?: string | null
  unitLabel?: string | null
  weightConversionFactor?: number | null
  packSize?: number | null
  unitTypes?: Array<'P' | 'W' | 'PA'> | null
  weightStepGrams?: number | null
  favorite?: boolean
  isBio?: boolean
  imageUrl?: string | null
  priceCents?: number | null
  priceText?: string | null
  backendUnit?: string | null
  /** Raw Collect&Go catalogEntryView — stored in cogo_product_history. */
  productview?: Record<string, unknown> | null
  productviewSource?: string | null
  productviewUrl?: string | null
  productviewSearchTerm?: string | null
}

function productviewFields(p: Record<string, unknown>) {
  return {
    productview:
      p.productview && typeof p.productview === 'object'
        ? (p.productview as Record<string, unknown>)
        : null,
    productviewSource:
      p.productviewSource != null ? String(p.productviewSource) : null,
    productviewUrl: p.productviewUrl != null ? String(p.productviewUrl) : null,
    productviewSearchTerm:
      p.productviewSearchTerm != null ? String(p.productviewSearchTerm) : null,
  }
}

function pickPriceCents(
  detail: Record<string, unknown>,
  tile: Record<string, unknown>
): number | null {
  if (typeof detail.priceCents === 'number') return detail.priceCents
  if (typeof tile.priceCents === 'number') return tile.priceCents
  const fromDetail = cogoPriceCentsFromText(
    detail.priceText != null ? String(detail.priceText) : null
  )
  if (fromDetail != null) return fromDetail
  return cogoPriceCentsFromText(
    tile.priceText != null ? String(tile.priceText) : null
  )
}

function asUnitTypes(raw: unknown): Array<'P' | 'W' | 'PA'> {
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (m): m is 'P' | 'W' | 'PA' => m === 'P' || m === 'W' || m === 'PA'
  )
}

/**
 * Resolve an explicit unitTypes list from tile/PDP fields without opening
 * extra pages. Packed bags / piece-count packs (20st) → P (unless dual
 * st/pak). Dual st/kg loose produce keeps both even when weightConversionFactor is WCF
 * piece weight. Ignore €/kg volume straplines when pack signals say fixed /st.
 */
function resolveUnitTypes(input: {
  unitTypes?: unknown
  weightConversionFactor?: number | null
  packSize?: number | null
  unitLabel?: string | null
  priceText?: string | null
}): Array<'P' | 'W' | 'PA'> {
  const modes = asUnitTypes(input.unitTypes)
  // Dual loose produce: weightConversionFactor may be average piece weight (WCF).
  if (modes.includes('P') && modes.includes('W')) {
    return ['P', 'W']
  }
  const weightConversionFactor = input.weightConversionFactor
  const packSize = input.packSize
  const fixedPack =
    (weightConversionFactor != null && weightConversionFactor > 0) ||
    (packSize != null && packSize > 1)
  if (fixedPack) {
    if (modes.includes('PA') && modes.includes('P')) {
      return ['P', 'PA']
    }
    return ['P']
  }
  if (modes.length > 0) return [...new Set(modes)]

  // Infer from shelf price only — strip volume straplines like "(€23,05 / kg)".
  const price = String(input.priceText || '')
    .replace(/\([^)]*\/\s*(?:kg|l|st|stuk)[^)]*\)/gi, ' ')
    .replace(/\d+[.,]\d+\s*euro per (?:kilo)?gram/gi, ' ')
    .replace(/\d+[.,]\d+\s*\/\s*kg\b/gi, ' ')
  const pricedPerKg = /\/\s*kg|per kilogram/i.test(price)
  const pricedPerStuk = /\/\s*st\b|per stuk/i.test(price)
  if (pricedPerKg && pricedPerStuk) return ['P', 'W']
  if (pricedPerKg) return ['W']
  if (pricedPerStuk) return ['P']
  if (packSize != null && packSize > 0) return ['P']
  if (/\bst\b|stuk/i.test(String(input.unitLabel || ''))) return ['P']
  return []
}

function backendUnitFromScrape(input: {
  backendUnit?: unknown
  priceText?: string | null
  unitTypes?: Array<'P' | 'W' | 'PA'>
}): string | null {
  if (
    input.backendUnit === 'kg' ||
    input.backendUnit === 'st' ||
    input.backendUnit === 'pak'
  ) {
    return input.backendUnit
  }
  const price = String(input.priceText || '')
  // Shelf "Prijs: € x,xx per kilogram" (not parenthetical volume).
  if (/Prijs:\s*€\s*[\d.,]+\s*per\s+kilogram/i.test(price)) return 'kg'
  if (/Prijs:\s*€\s*[\d.,]+\s*per\s+stuk/i.test(price)) return 'st'
  const modes = input.unitTypes || []
  if (modes.includes('W') && !modes.includes('P') && !modes.includes('PA')) {
    return 'kg'
  }
  return null
}

function tileToPartial(p: Record<string, unknown>) {
  const weightConversionFactor =
    typeof p.weightConversionFactor === 'number'
      ? p.weightConversionFactor
      : null
  const packSize = typeof p.packSize === 'number' ? p.packSize : null
  const unitLabel = p.unitLabel != null ? String(p.unitLabel) : null
  const priceText = p.priceText != null ? String(p.priceText) : null
  const unitTypes = resolveUnitTypes({
    unitTypes: p.unitTypes,
    weightConversionFactor,
    packSize,
    unitLabel,
    priceText,
  })
  return {
    assortmentUrl: p.assortmentUrl != null ? String(p.assortmentUrl) : '',
    technicalProductId:
      p.technicalProductId != null ? String(p.technicalProductId) : '',
    display: p.display != null ? String(p.display) : '',
    unitLabel,
    weightConversionFactor,
    packSize,
    unitTypes,
    weightStepGrams:
      typeof p.weightStepGrams === 'number' ? p.weightStepGrams : null,
    favorite: Boolean(p.favorite),
    isBio: Boolean(p.isBio),
    imageUrl: p.imageUrl != null ? String(p.imageUrl) : null,
    priceText,
    backendUnit: backendUnitFromScrape({
      backendUnit: p.backendUnit,
      priceText,
      unitTypes,
    }),
  }
}

/**
 * Build an EnrichArticle from a zoeken tile when it already has a TPC id.
 * Search tiles expose data-technical-product-id — no PDP visit needed.
 */
function articleFromSearchTile(
  p: Record<string, unknown>
): EnrichArticle | null {
  const partial = tileToPartial(p)
  // Sync needs a real PDP — skip API-only hits without /assortiment/ URL.
  const url = (partial.assortmentUrl || '').trim()
  if (
    !partial.technicalProductId ||
    !partial.display ||
    !url ||
    !/\/assortiment\//i.test(url) ||
    /rootCategoryId=/i.test(url)
  ) {
    return null
  }
  return {
    technicalProductId: partial.technicalProductId,
    display: partial.display,
    assortmentUrl: url.split('?')[0]!,
    unitLabel: partial.unitLabel,
    weightConversionFactor: partial.weightConversionFactor,
    packSize: partial.packSize,
    unitTypes: partial.unitTypes,
    weightStepGrams: partial.weightStepGrams,
    favorite: partial.favorite,
    isBio: partial.isBio || /\bbio\b/i.test(partial.display),
    imageUrl: partial.imageUrl,
    priceCents: pickPriceCents({}, p),
    priceText: partial.priceText,
    backendUnit: partial.backendUnit,
    ...productviewFields(p),
  }
}

async function articleFromSearchHit(
  p: Record<string, unknown>
): Promise<EnrichArticle | null> {
  return articleFromSearchTile(p) || scrapeHit(p)
}

function normUrl(url: string): string {
  return url.split('?')[0]!.replace(/\/$/, '').toLowerCase()
}

function urlsMatch(a: string, b: string): boolean {
  const x = normUrl(a)
  const y = normUrl(b)
  return x === y || x.endsWith(y) || y.endsWith(x)
}

async function scrapeHit(
  p: Record<string, unknown>
): Promise<EnrichArticle | null> {
  const url = String(p.assortmentUrl || '')
  if (!url) return null
  await cogoCommand({ type: 'navigate', url, active: false })
  const detail = await cogoCommand({
    type: 'scrapeProductDetail',
    assortmentUrl: url,
  })
  if (detail.blocked) throw new Error('blocked')
  if (detail.status === 'needs_login') throw new Error('needs_login')
  if (detail.status === 'wrong_page' || detail.ok === false) return null
  const gotUrl =
    detail.assortmentUrl != null ? String(detail.assortmentUrl) : ''
  if (gotUrl && !urlsMatch(gotUrl, url)) return null
  const technicalProductId =
    detail.technicalProductId != null
      ? String(detail.technicalProductId)
      : p.technicalProductId != null
        ? String(p.technicalProductId)
        : ''
  if (!technicalProductId) return null
  const weightConversionFactor =
    typeof detail.weightConversionFactor === 'number'
      ? detail.weightConversionFactor
      : typeof p.weightConversionFactor === 'number'
        ? p.weightConversionFactor
        : null
  const packSize =
    typeof detail.packSize === 'number'
      ? detail.packSize
      : typeof p.packSize === 'number'
        ? p.packSize
        : null
  const unitLabel =
    detail.unitLabel != null
      ? String(detail.unitLabel)
      : p.unitLabel != null
        ? String(p.unitLabel)
        : null
  const priceText =
    detail.priceText != null
      ? String(detail.priceText)
      : p.priceText != null
        ? String(p.priceText)
        : null
  const unitTypes = resolveUnitTypes({
    unitTypes: detail.unitTypes ?? p.unitTypes,
    weightConversionFactor,
    packSize,
    unitLabel,
    priceText,
  })
  return {
    technicalProductId,
    display: String(detail.display || p.display || ''),
    assortmentUrl: String(detail.assortmentUrl || url),
    unitLabel,
    weightConversionFactor,
    packSize,
    unitTypes,
    weightStepGrams:
      typeof detail.weightStepGrams === 'number'
        ? detail.weightStepGrams
        : null,
    favorite: Boolean(detail.favorite || p.favorite),
    isBio:
      Boolean(detail.isBio) ||
      /\bbio\b/i.test(String(detail.display || p.display || '')),
    imageUrl:
      detail.imageUrl != null
        ? String(detail.imageUrl)
        : p.imageUrl != null
          ? String(p.imageUrl)
          : null,
    priceCents: pickPriceCents(detail as Record<string, unknown>, p),
    priceText,
    backendUnit: backendUnitFromScrape({
      backendUnit: detail.backendUnit ?? p.backendUnit,
      priceText,
      unitTypes,
    }),
    ...productviewFields({
      ...p,
      ...detail,
      productview: detail.productview ?? p.productview,
      productviewSource: detail.productviewSource ?? p.productviewSource,
      productviewUrl: detail.productviewUrl ?? p.productviewUrl,
      productviewSearchTerm:
        detail.productviewSearchTerm ?? p.productviewSearchTerm,
    }),
  }
}

async function enrichOne(display: string): Promise<EnrichArticle[]> {
  await cogoEnsureTab({ active: false })
  await cogoCommand({
    type: 'navigate',
    url: COGO_SEARCH_URL(display),
    active: false,
  })
  const search = await cogoCommand({
    type: 'scrapeSearchResults',
    searchTerm: display,
  })
  if (search.blocked || search.status === 'needs_login') {
    throw new Error(search.status === 'needs_login' ? 'needs_login' : 'blocked')
  }
  if (
    search.status === 'wrong_search' ||
    search.status === 'timeout' ||
    search.ok === false
  ) {
    return []
  }
  const products = Array.isArray(search.products)
    ? (search.products as Array<Record<string, unknown>>)
    : []
  const top = products
    .filter((p) => p.technicalProductId || p.assortmentUrl)
    .slice(0, TOP_N)

  const out: EnrichArticle[] = []
  for (const p of top) {
    const art = await articleFromSearchHit(p)
    if (art) out.push(art)
  }
  return out
}

/**
 * Broader zoeken for one Menu product, skipping already-shown TPCs / URLs.
 * Used by OrderDetail “Meer artikelen”.
 */
export async function searchMoreCogoArticles(opts: {
  display: string
  excludeTechnicalProductIds?: string[]
  excludeUrls?: string[]
  limit?: number
}): Promise<EnrichArticle[]> {
  const excludeTpc = new Set(
    (opts.excludeTechnicalProductIds || []).filter(Boolean)
  )
  const excludeUrls = new Set(
    (opts.excludeUrls || [])
      .filter(Boolean)
      .map((u) => u.split('?')[0]!.toLowerCase())
  )
  const limit = opts.limit ?? TOP_N

  return withCogoTabLock(`searchMore:${opts.display}`, async () => {
    beginCogoAutomation('searchMore')
    try {
      await cogoEnsureTab({ active: false })
      const auth = await cogoCheckAuth()
      if (auth.blocked) throw new Error('blocked')
      if (!auth.loggedIn) throw new Error('needs_login')

      await cogoCommand({
        type: 'navigate',
        url: COGO_SEARCH_URL(opts.display),
        active: false,
      })
      const search = await cogoCommand({
        type: 'scrapeSearchResults',
        searchTerm: opts.display,
      })
      if (search.blocked || search.status === 'needs_login') {
        throw new Error(
          search.status === 'needs_login' ? 'needs_login' : 'blocked'
        )
      }
      if (
        search.status === 'wrong_search' ||
        search.status === 'timeout' ||
        search.ok === false
      ) {
        return []
      }
      const products = Array.isArray(search.products)
        ? (search.products as Array<Record<string, unknown>>)
        : []

      const candidates = products
        .filter((p) => p.technicalProductId || p.assortmentUrl)
        .map(tileToPartial)
        .filter((p) => {
          const url = (p.assortmentUrl || '').split('?')[0]!.toLowerCase()
          if (url && excludeUrls.has(url)) return false
          if (p.technicalProductId && excludeTpc.has(p.technicalProductId))
            return false
          return Boolean(p.technicalProductId || p.assortmentUrl)
        })
        .slice(0, SEARCH_SCAN)

      const out: EnrichArticle[] = []
      for (const p of candidates) {
        if (out.length >= limit) break
        const art = await articleFromSearchHit(
          p as unknown as Record<string, unknown>
        )
        if (!art) continue
        if (excludeTpc.has(art.technicalProductId)) continue
        const url = (art.assortmentUrl || '').split('?')[0]!.toLowerCase()
        if (url && excludeUrls.has(url)) continue
        out.push(art)
        excludeTpc.add(art.technicalProductId)
      }
      return out
    } finally {
      endCogoAutomation('searchMore')
    }
  })
}

export type CogoEnrichPhase =
  | 'off'
  | 'idle'
  | 'waiting_ext'
  | 'busy_sync'
  | 'enriching'
  | 'error'

export type CogoEnrichStatus = {
  enabled: boolean
  phase: CogoEnrichPhase
  currentDisplay: string | null
  currentProductId: number | null
  queueLength: number | null
  lastError: string | null
  updatedAt: number
}

const listeners = new Set<(s: CogoEnrichStatus) => void>()

let status: CogoEnrichStatus = {
  enabled: true,
  phase: 'idle',
  currentDisplay: null,
  currentProductId: null,
  queueLength: null,
  lastError: null,
  updatedAt: Date.now(),
}

function setStatus(patch: Partial<CogoEnrichStatus>) {
  const next: CogoEnrichStatus = { ...status, ...patch, updatedAt: Date.now() }
  if (
    Object.prototype.hasOwnProperty.call(patch, 'currentDisplay') &&
    patch.currentDisplay == null &&
    !Object.prototype.hasOwnProperty.call(patch, 'currentProductId')
  ) {
    next.currentProductId = null
  }
  status = next
  for (const cb of listeners) cb(status)
}

export function getCogoEnrichStatus(): CogoEnrichStatus {
  return status
}

export function subscribeCogoEnrichStatus(
  cb: (s: CogoEnrichStatus) => void
): () => void {
  listeners.add(cb)
  cb(status)
  return () => {
    listeners.delete(cb)
  }
}

/** Short Dutch label for chrome / settings. */
export function formatCogoEnrichLabel(s: CogoEnrichStatus): string {
  if (!s.enabled || s.phase === 'off') return 'Verrijken uit'
  if (s.phase === 'enriching') {
    const name = s.currentDisplay?.trim()
    const q =
      s.queueLength != null && s.queueLength > 0
        ? `${s.queueLength} in wachtrij · `
        : ''
    return name ? `${q}Bezig met ${name}` : `${q}Nadenken...`
  }
  if (s.phase === 'busy_sync') return 'Verrijken gepauzeerd (sync)'
  if (s.phase === 'waiting_ext') return 'Verrijken wacht (extensie/login)'
  if (s.phase === 'error' && s.lastError) {
    return `Verrijken fout · ${s.lastError}`
  }
  if (s.queueLength != null && s.queueLength > 0) {
    return `${s.queueLength} in wachtrij`
  }
  return 'Verrijken klaar'
}

async function fetchQueueLength(): Promise<number | null> {
  try {
    const res = await fetch('/api/cogo/enrich/queue', {
      credentials: 'include',
    })
    if (!res.ok) return null
    const data = (await res.json()) as { queue?: unknown[] }
    return Array.isArray(data.queue) ? data.queue.length : null
  } catch {
    return null
  }
}

async function claim(workerId: string) {
  const res = await fetch('/api/cogo/enrich/claim', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ workerId }),
  })
  if (!res.ok) return null
  const data = (await res.json()) as {
    item?: {
      claimId: string
      productId: number
      display: string
    } | null
  }
  return data.item ?? null
}

async function complete(
  workerId: string,
  claimId: string,
  articles: EnrichArticle[],
  error?: string
) {
  const res = await fetch('/api/cogo/enrich/result', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ workerId, claimId, articles, error }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(
      `Enrich result mislukt (${res.status})${text ? `: ${text.slice(0, 160)}` : ''}`
    )
  }
}

let running = false
let abort: AbortController | null = null
let workerId = crypto.randomUUID()
let unsubEnabled: (() => void) | null = null

/**
 * Start the global enrich loop (idempotent). Call from app shell when
 * the user is authenticated.
 */
export function startCogoEnrichWorker() {
  if (running) return
  running = true
  abort = new AbortController()
  const signal = abort.signal
  setStatus({ enabled: isCogoEnrichEnabled(), phase: 'idle' })

  unsubEnabled = subscribeCogoEnrichEnabled((enabled) => {
    setStatus({
      enabled,
      phase: enabled ? (status.phase === 'off' ? 'idle' : status.phase) : 'off',
      currentDisplay: enabled ? status.currentDisplay : null,
    })
    if (!enabled) endCogoAutomation('enrich')
  })

  void (async () => {
    while (!signal.aborted) {
      try {
        if (!isCogoEnrichEnabled()) {
          endCogoAutomation('enrich')
          setStatus({
            enabled: false,
            phase: 'off',
            currentDisplay: null,
          })
          await sleep(IDLE_MS)
          continue
        }

        const ext = await probeCogoExtensionFast()
        if (!ext.installed) {
          endCogoAutomation('enrich')
          setStatus({
            enabled: true,
            phase: 'waiting_ext',
            currentDisplay: null,
            queueLength: await fetchQueueLength(),
          })
          await sleep(IDLE_MS)
          continue
        }

        const queueLengthPeek = await fetchQueueLength()
        if (queueLengthPeek === 0) {
          endCogoAutomation('enrich')
          setStatus({
            enabled: true,
            phase: 'idle',
            currentDisplay: null,
            queueLength: 0,
            lastError: null,
          })
          await sleep(IDLE_MS)
          continue
        }

        if (ext.cogoTabCount === 0) {
          beginCogoAutomation('enrich')
          setStatus({
            enabled: true,
            phase: 'waiting_ext',
            currentDisplay: null,
            queueLength: queueLengthPeek,
          })
          const ensured = await cogoEnsureTab({ active: false })
          if (ensured.ok === false || ensured.status === 'timeout') {
            await sleep(IDLE_MS)
            continue
          }
        }
        if (ext.loggingIn) {
          beginCogoAutomation('enrich')
          setStatus({
            enabled: true,
            phase: 'waiting_ext',
            currentDisplay: null,
          })
          await sleep(IDLE_MS)
          continue
        }
        if (isCogoTabBusy()) {
          setStatus({
            enabled: true,
            phase: 'busy_sync',
            currentDisplay: null,
          })
          await sleep(IDLE_MS)
          continue
        }
        const auth = await cogoCheckAuth()
        if (!auth.loggedIn || auth.blocked || auth.loggingIn) {
          beginCogoAutomation('enrich')
          setStatus({
            enabled: true,
            phase: 'waiting_ext',
            currentDisplay: null,
            queueLength: await fetchQueueLength(),
          })
          await sleep(IDLE_MS)
          continue
        }

        if (!isCogoEnrichEnabled()) continue

        const item = await claim(workerId)
        if (!item) {
          endCogoAutomation('enrich')
          // Another tab may hold the claim — keep the real queue length in UI.
          setStatus({
            enabled: true,
            phase: 'idle',
            currentDisplay: null,
            queueLength: await fetchQueueLength(),
            lastError: null,
          })
          await sleep(IDLE_MS)
          continue
        }

        beginCogoAutomation('enrich')
        const queueLength = await fetchQueueLength()
        setStatus({
          enabled: true,
          phase: 'enriching',
          currentDisplay: item.display,
          currentProductId: item.productId,
          queueLength,
          lastError: null,
        })

        try {
          const articles = await withCogoTabLock(`enrich:${item.display}`, () =>
            enrichOne(item.display)
          )
          if (signal.aborted) return
          await complete(workerId, item.claimId, articles)
          setStatus({
            enabled: isCogoEnrichEnabled(),
            phase: isCogoEnrichEnabled() ? 'idle' : 'off',
            currentDisplay: null,
            currentProductId: null,
            queueLength: await fetchQueueLength(),
            lastError: null,
          })
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e)
          if (msg === 'needs_login' || msg === 'blocked') {
            await complete(workerId, item.claimId, [], msg)
            setStatus({
              enabled: true,
              phase: 'waiting_ext',
              currentDisplay: null,
              currentProductId: null,
              lastError: msg,
            })
            await sleep(IDLE_MS * 2)
            continue
          }
          await complete(workerId, item.claimId, [], msg)
          setStatus({
            enabled: true,
            phase: 'error',
            currentDisplay: null,
            lastError: msg,
            queueLength: await fetchQueueLength(),
          })
        }
        // Navigations / qty changes share the extension 50/min gate with basket sync.
      } catch {
        await sleep(IDLE_MS)
      }
    }
    endCogoAutomation('enrich')
  })()
}

export function stopCogoEnrichWorker() {
  abort?.abort()
  abort = null
  running = false
  unsubEnabled?.()
  unsubEnabled = null
  endCogoAutomation('enrich')
  setStatus({
    phase: 'idle',
    currentDisplay: null,
  })
}

// Vite HMR leaves the old loop alive while RequireAuth does not remount —
// dispose + restart so enrich does not look "stuck" after editing this module.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    stopCogoEnrichWorker()
  })
  import.meta.hot.accept(() => {
    startCogoEnrichWorker()
  })
}
